
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradingAccountResponse struct
 */
class TradingAccountResponse {
  /**
   * Initialize struct
   * @param {!string=} id
   * @param {!number=} login
   * @param {!string=} userId
   * @param {!string=} error
   * @param {!boolean=} succeeded
   * @constructor
   */
  constructor (id = '', login = new UInt64(0, 0), userId = '', error = '', succeeded = false) {
    this.id = id
    this.login = login
    this.userId = userId
    this.error = error
    this.succeeded = succeeded
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingAccountResponse}
   * @param {!TradingAccountResponse} other Other struct
   * @returns {!TradingAccountResponse} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = other.id
    } else {
      this.id = null
    }
    if (other.login != null) {
      this.login = UInt64.fromNumber(other.login)
    } else {
      this.login = null
    }
    if (other.userId != null) {
      this.userId = other.userId
    } else {
      this.userId = null
    }
    if (other.error != null) {
      this.error = other.error
    } else {
      this.error = null
    }
    if (other.succeeded != null) {
      this.succeeded = other.succeeded
    } else {
      this.succeeded = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingAccountResponse}
   * @returns {!TradingAccountResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingAccountResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingAccountResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingAccountResponse}
   * @param {!TradingAccountResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingAccountResponse)) {
      throw new TypeError('Instance of TradingAccountResponse is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingAccountResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id : null),
      login: ((this.login != null) ? this.login.toNumber() : null),
      userId: ((this.userId != null) ? this.userId : null),
      error: ((this.error != null) ? this.error : null),
      succeeded: ((this.succeeded != null) ? this.succeeded : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingAccountResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingAccountResponse} other Object value
   * @returns {!TradingAccountResponse} Created struct
   */
  static fromObject (other) {
    return new TradingAccountResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingAccountResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingAccountResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingAccountResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10012
  }
}

export { TradingAccountResponse };

/**
 * Fast Binary Encoding TradingAccountResponse field model
 */
class FieldModelTradingAccountResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelString(buffer, 4 + 4)
    this.login = new fbe.FieldModelUInt64(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.userId = new fbe.FieldModelString(buffer, this.login.fbeOffset + this.login.fbeSize)
    this.error = new fbe.FieldModelString(buffer, this.userId.fbeOffset + this.userId.fbeSize)
    this.succeeded = new fbe.FieldModelBool(buffer, this.error.fbeOffset + this.error.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingAccountResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingAccountResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.login.fbeSize + this.userId.fbeSize + this.error.fbeSize + this.succeeded.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingAccountResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.login.fbeExtra + this.userId.fbeExtra + this.error.fbeExtra + this.succeeded.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingAccountResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingAccountResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingAccountResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10012
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingAccountResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingAccountResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.login.fbeSize

    if ((fbeCurrentSize + this.userId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.userId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.userId.fbeSize

    if ((fbeCurrentSize + this.error.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.error.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.error.fbeSize

    if ((fbeCurrentSize + this.succeeded.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.succeeded.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.succeeded.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingAccountResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingAccountResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingAccountResponse}
   * @param {!TradingAccountResponse} fbeValue Default value, defaults is new TradingAccountResponse()
   * @returns {!TradingAccountResponse} TradingAccountResponse value
   */
  get (fbeValue = new TradingAccountResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingAccountResponse}
   * @param {!TradingAccountResponse} fbeValue TradingAccountResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get()
    } else {
      fbeValue.id = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.login.fbeSize) <= fbeStructSize) {
      fbeValue.login = this.login.get()
    } else {
      fbeValue.login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.login.fbeSize

    if ((fbeCurrentSize + this.userId.fbeSize) <= fbeStructSize) {
      fbeValue.userId = this.userId.get()
    } else {
      fbeValue.userId = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.userId.fbeSize

    if ((fbeCurrentSize + this.error.fbeSize) <= fbeStructSize) {
      fbeValue.error = this.error.get()
    } else {
      fbeValue.error = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.error.fbeSize

    if ((fbeCurrentSize + this.succeeded.fbeSize) <= fbeStructSize) {
      fbeValue.succeeded = this.succeeded.get()
    } else {
      fbeValue.succeeded = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.succeeded.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingAccountResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingAccountResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingAccountResponse}
   * @param {!TradingAccountResponse} fbeValue TradingAccountResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingAccountResponse}
   * @param {TradingAccountResponse} fbeValue TradingAccountResponse value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.login.set(fbeValue.login)
    this.userId.set(fbeValue.userId)
    this.error.set(fbeValue.error)
    this.succeeded.set(fbeValue.succeeded)
  }
}

export { FieldModelTradingAccountResponse };

/**
 * Fast Binary Encoding TradingAccountResponse model
 */
class TradingAccountResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingAccountResponse(this.buffer, 4)
  }

  /**
   * Get the TradingAccountResponse model
   * @this {!TradingAccountResponseModel}
   * @returns {!FieldModelTradingAccountResponse} model TradingAccountResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingAccountResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingAccountResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingAccountResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingAccountResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingAccountResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingAccountResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingAccountResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingAccountResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingAccountResponseModel}
   * @param {!TradingAccountResponse} value TradingAccountResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingAccountResponseModel}
   * @param {!TradingAccountResponse} value TradingAccountResponse value, defaults is new TradingAccountResponse()
   * @return {!object} Deserialized TradingAccountResponse value and its size
   */
  deserialize (value = new TradingAccountResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingAccountResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingAccountResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingAccountResponseModel}
   * @param {!number} prev Previous TradingAccountResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingAccountResponseModel };
